import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { DashboardContent } from './DashboardContent';
import { DashboardHeader } from './DashboardHeader';

interface UserProfile {
  user_id: string;
  onboarding_completed: boolean;
  full_name?: string;
}

export function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<UserProfile | null>(null);

  useEffect(() => {
    async function loadProfile() {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);
        
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (profileError) {
          if (profileError.code === 'PGRST116') {
            const { data: newProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert([{
                user_id: user.id,
                onboarding_completed: false
              }])
              .select()
              .single();

            if (createError) throw createError;
            setProfileData(newProfile);
          } else {
            throw profileError;
          }
        } else {
          setProfileData(profile);
        }

      } catch (error) {
        console.error('Error loading profile:', error);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4 text-center">{error}</div>;
  }

  if (!profileData?.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 py-8">
        <DashboardHeader profile={profileData} />
        <DashboardContent />
      </div>
    </motion.div>
  );
}